import React from "react";
import { AlertTriangle, Clock, DollarSign, Users } from "lucide-react";

const Problem = () => {
  const problems = [
    {
      icon: <AlertTriangle className="w-8 h-8 text-emerald-500" />,
      title: "Security Chaos",
      metric: "40%",
      metricLabel: "of security time wasted on duplicates",
      description: "Multiple tools generate thousands of alerts daily with duplicate findings causing severe alert fatigue.",
      metricColor: "text-emerald-500"
    },
    {
      icon: <Clock className="w-8 h-8 text-blue-500" />,
      title: "No Risk Prioritization",
      metric: "Months",
      metricLabel: "critical vulnerabilities unaddressed",
      description: "Critical issues buried in noise with no way to distinguish what matters most.",
      metricColor: "text-blue-500"
    },
    {
      icon: <DollarSign className="w-8 h-8 text-orange-500" />,
      title: "Cost of Inaction", 
      metric: "Millions",
      metricLabel: "in potential breach costs",
      description: "Expensive security tools without measurable ROI, leading to breaches and compliance fines.",
      metricColor: "text-orange-500"
    },
    {
      icon: <Users className="w-8 h-8 text-purple-500" />,
      title: "Accountability Gap",
      metric: "0%",
      metricLabel: "visibility into open risks",
      description: "No clear ownership, manual follow-ups lead to delays and unresolved issues in production.",
      metricColor: "text-purple-500"
    }
  ];

  return (
    <section id="problem" className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            The Security Crisis Is
            <span className="text-blue-600 block">Getting Worse</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Vulnerability backlogs keep growing, and security teams are drowning in noise while attackers move faster than ever.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {problems.map((problem, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {problem.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {problem.title}
                    </h3>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-4xl font-bold ${problem.metricColor} mb-1`}>
                    {problem.metric}
                  </div>
                  <div className="text-gray-700 font-medium text-sm">
                    {problem.metricLabel}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {problem.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom Message */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-sm">
          <p className="text-center text-base text-gray-900">
            Manual triage is time-consuming, costly, and struggles to scale with the volume of vulnerabilities. Tool fragmentation, overwhelming alerts, and inefficient follow-ups create a perfect storm where critical vulnerabilities remain unaddressed while teams chase false alarms.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Problem;
